import { ComponentType, Suspense, lazy } from "react";

import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { useAppSelector } from "store/index";
import { selectCurrentSectionAvEnabled, selectIsClassSessionVirtual } from "store/selectors";
import { selectCurrentClassUserIsInClassSession } from "store/selectors/class-user";
import {
  selectIsObservingVirtualClass,
  selectStableIsMainAvTab,
} from "store/selectors/pubsub-virtual-class-state";
import { isAVEnabled } from "utils/waffle-flags";

const createAvWrapper = (
  importFunc: () => Promise<{ default: ComponentType<unknown> }>,
  shouldRender: () => boolean,
  fallbackText = t("av.loading_av_subsystem"),
) => {
  const LazyComponent = lazy(importFunc);

  const WrappedComponent = (props: Record<string, unknown>) => {
    if (!shouldRender()) {
      return null;
    }

    return (
      <Suspense
        fallback={
          <div className="fixed bottom-0 right-0 w-[128px] flex-shrink-0 flex-grow-0 bg-white p-2">
            {fallbackText}
          </div>
        }
      >
        <LazyComponent {...props} />
      </Suspense>
    );
  };

  // Set a display name for easier debugging
  WrappedComponent.displayName = `withAVSubsystem(${importFunc.name || "Anonymous"})`;

  return WrappedComponent;
};

export const withActiveAv = (
  importFunc: () => Promise<{ default: ComponentType<unknown> }>,
  fallbackText = t("av.loading_av_subsystem"),
  shouldRenderWhenObserving = false,
) => {
  return createAvWrapper(
    importFunc,
    () => {
      const isVirtualSessionInProgress = useAppSelector(selectIsClassSessionVirtual);
      const isAvEnabled = isAVEnabled();
      const isProjector = isProjectorView();
      const isInClassSession = useAppSelector(selectCurrentClassUserIsInClassSession);
      const mainAvTab = useAppSelector(selectStableIsMainAvTab);
      const isInVirtualClassInThisTab = isInClassSession && mainAvTab;
      const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
      const shouldRender =
        isInVirtualClassInThisTab || (shouldRenderWhenObserving && isObservingClass);
      return !isProjector && isAvEnabled && isVirtualSessionInProgress && shouldRender;
    },
    fallbackText,
  );
};

export const withEnabledAv = (
  importFunc: () => Promise<{ default: ComponentType<unknown> }>,
  fallbackText = t("av.loading_av_subsystem"),
) => {
  return createAvWrapper(
    importFunc,
    () => {
      const avEnabled = useAppSelector(selectCurrentSectionAvEnabled);
      const isMobile = !useIsMdOrLarger();
      return isAVEnabled() && avEnabled && !isMobile;
    },
    fallbackText,
  );
};
